import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

export type ReactHookFormProps<TFormValues = {}> = {
  Controller: any, // React component from react-hook-form
  control: any, // Control object from useForm
  getValues: () => TFormValues, // Function to get form values
  setError: (
    name: $Keys<TFormValues>,
    error: { type: string, message: string },
  ) => void, // Function to set field errors
  clearErrors: (name?: $Keys<TFormValues>) => void, // Function to clear field errors
  errors: $ObjMapi<TFormValues, <K>(K) => { message: string, type: string }>, // Form errors object
  getFieldState: (name: $Keys<TFormValues>) => {
    error?: { message: string, type: string },
    isDirty: boolean,
    isTouched: boolean,
  }, // Function to get field state
  handleSubmit: (
    onValid: (data: TFormValues) => void,
    onInvalid?: (errors: Object) => void,
  ) => (e?: mixed) => void, // Function to handle form submission
  isValid: boolean, // Whether form is valid
  isDirty: boolean, // Whether form has been modified
  isSubmitting: boolean, // Whether form is currently submitting
  touchedFields: $ObjMapi<TFormValues, <K>(K) => boolean>, // Object tracking which fields have been touched
  dirtyFields: $ObjMapi<TFormValues, <K>(K) => boolean>, // Object tracking which fields have been modified
  setValue: (
    name: $Keys<TFormValues>,
    value: $ElementType<TFormValues, $Keys<TFormValues>>,
  ) => void, // Function to set field values
  trigger: (name?: $Keys<TFormValues>) => Promise<boolean>, // Function to trigger validation
  reset: (values?: $Partial<TFormValues>) => void, // Function to reset form
  unregister: (name: $Keys<TFormValues>) => void, // Function to unregister fields
  watch: (
    name?: $Keys<TFormValues>,
  ) => $ElementType<TFormValues, $Keys<TFormValues>> | TFormValues, // Function to watch field values
};

const useReactHookFormProps = (
  { defaultValues, values, resetOptions, mode, shouldUnregister } = {
    defaultValues: {},
    mode: 'onBlur',
  },
) => {
  const {
    handleSubmit,
    control,
    getValues,
    getFieldState,
    formState: {
      errors,
      isValid,
      isDirty,
      isSubmitting,
      touchedFields,
      dirtyFields,
    },
    setError,
    setValue,
    clearErrors,
    trigger,
    reset,
    unregister,
    watch,
  } = useForm({
    defaultValues,
    values,
    resetOptions,
    mode,
    shouldUnregister,
  });

  return useMemo(
    () => ({
      Controller,
      control,
      getValues,
      setError,
      clearErrors,
      errors,
      getFieldState,
      handleSubmit,
      isValid,
      isDirty,
      isSubmitting,
      touchedFields,
      dirtyFields,
      setValue,
      trigger,
      reset,
      unregister,
      watch,
    }),
    [
      clearErrors,
      control,
      errors,
      getFieldState,
      getValues,
      handleSubmit,
      isDirty,
      touchedFields,
      dirtyFields,
      isSubmitting,
      isValid,
      setError,
      setValue,
      trigger,
      reset,
      unregister,
      watch,
    ],
  );
};

export default useReactHookFormProps;
